<template>
    <div class="container mx-auto p-6 dark-bg">
      <div class="card shadow-lg p-8 mb-10">
        <h1 class="text-3xl font-bold mb-6 text-white">📚 Class Subjects Overview</h1>
  
        <!-- Selections Card -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div class="selection-card p-6 rounded-lg shadow-inner">
            <h2 class="text-xl font-semibold text-white mb-4">Class & Section Selection</h2>
  
            <label class="text-gray-300 text-sm mb-2 block">Select Class</label>
            <el-select
              v-model="selectedClass"
              placeholder="Select Class"
              filterable
              class="custom-select"
              @change="fetchClassDetails"
            >
              <el-option
                v-for="classItem in classes"
                :key="classItem.id"
                :label="classItem.name"
                :value="classItem.id"
              />
            </el-select>
  
            <div v-if="selectedClass" class="mt-6">
              <label class="text-gray-300 text-sm mb-2 block">Select Section (Optional)</label>
              <el-select
                v-model="selectedSection"
                placeholder="Select Section"
                filterable
                clearable
                class="custom-select"
                @change="fetchClassDetails"
              >
                <el-option
                  v-for="section in sections"
                  :key="section.id"
                  :label="section.name"
                  :value="section.id"
                />
              </el-select>
            </div>
          </div>
  
          <div v-if="selectedClass" class="selection-card p-6 rounded-lg shadow-inner">
            <h2 class="text-xl font-semibold text-white mb-4">Subjects Selection</h2>
  
            <label class="text-gray-300 text-sm mb-2 block">Select Subjects</label>
            <el-select
              v-model="selectedSubjects"
              multiple
              filterable
              placeholder="Select Subjects"
              class="custom-select"
            >
              <el-option
                v-for="subject in allSubjects"
                :key="subject.id"
                :label="subject.name"
                :value="subject.id"
              />
            </el-select>
  
            <!-- Action Buttons -->
            <div class="flex gap-4 mt-6">
              <el-button
                type="primary"
                :disabled="!selectedClass || selectedSubjects.length === 0"
                @click="assignSubjects"
                class="action-btn"
              >
                Assign
              </el-button>
  
              <el-button
                type="danger"
                :disabled="!selectedClass || selectedSubjects.length === 0"
                @click="removeSubjects"
                class="action-btn"
              >
                Remove
              </el-button>
            </div>
          </div>
        </div>
  
  <!-- Overview Table -->
<div class="mt-14">
  <h2 class="text-2xl font-semibold mb-6 text-white">📋 Classes and Their Subjects</h2>

  <div class="table-container overflow-x-auto rounded-lg shadow-md">
    <table class="w-full text-left table-auto dark-table">
      <thead>
        <tr>
          <th>Class</th>
          <th>Subjects</th>
          <th>Sections</th>
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="classItem in classes" :key="classItem.id" class="hover:bg-blue-900 transition">
          <td class="font-semibold">{{ classItem.name }}</td>
          <td>
            <div class="flex flex-wrap gap-2">
              <el-tag
                v-for="subject in classItem.subjects"
                :key="subject.id"
                closable
                @close="removeSingleSubject(subject.id, classItem.id)"
                class="tag"
              >
                {{ subject.name }}
              </el-tag>
            </div>
          </td>
          <td>
            <div v-for="section in classItem.sections" :key="section.id" class="mb-2">
              <div class="font-semibold text-sm mb-1">{{ section.name }}</div>
              <div class="flex flex-wrap gap-2">
                <el-tag
                  v-for="subject in section.subjects"
                  :key="subject.id"
                  closable
                  @close="removeSingleSubject(subject.id, classItem.id, section.id)"
                  class="tag"
                >
                  {{ subject.name }}
                </el-tag>
              </div>
            </div>
          </td>
          <td>
            <el-button size="small" @click="selectClass(classItem.id)" class="edit-btn">
              Edit
            </el-button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</div>

  
      </div>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  
  const api = axios.create({
    baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api',
  });
  
  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  }, (error) => Promise.reject(error));
  
  export default {
    data() {
      return {
        classes: [],
        allSubjects: [],
        sections: [],
        selectedClass: null,
        selectedSection: null,
        selectedSubjects: [],
      };
    },
    async created() {
      await this.fetchData();
    },
    methods: {
      async fetchData() {
        try {
          const [classesRes, subjectsRes] = await Promise.all([
            api.get('/class-subjects'),
            api.get('/subjects')
          ]);
          this.classes = classesRes.data.classes;
          this.allSubjects = subjectsRes.data.data;
        } catch (error) {
          console.error('Error fetching data:', error);
          this.$message.error('Failed to load data');
        }
      },
  
      selectClass(classId) {
        this.selectedClass = classId;
        this.fetchClassDetails();
      },
  
      async fetchClassDetails() {
        if (!this.selectedClass) return;
        try {
          const sectionsRes = await api.get(`/classes/${this.selectedClass}/sections`);
          this.sections = sectionsRes.data.sections || [];
          const selectedClassData = this.classes.find(c => c.id === this.selectedClass);
          if (selectedClassData) {
            this.currentAssignments = selectedClassData.subjects || [];
          }
        } catch (error) {
          console.error('Error fetching class details:', error);
          this.$message.error('Failed to load class details');
        }
      },
  
      async assignSubjects() {
        try {
          await api.post('/class-subjects', {
            class_id: this.selectedClass,
            section_id: this.selectedSection,
            subject_ids: this.selectedSubjects
          });
          this.$message.success('Subjects assigned successfully');
          this.selectedSubjects = [];
          await this.fetchData();
        } catch (error) {
          console.error('Error assigning subjects:', error);
          this.$message.error('Failed to assign subjects');
        }
      },
  
      async removeSubjects() {
        try {
          await api.delete('/class-subjects', {
            params: {
              class_id: this.selectedClass,
              section_id: this.selectedSection,
              subject_ids: this.selectedSubjects.join(',')
            }
          });
          this.$message.success('Subjects removed successfully');
          this.selectedSubjects = [];
          await this.fetchData();
        } catch (error) {
          console.error('Error removing subjects:', error);
          this.$message.error('Failed to remove subjects');
        }
      },
  
      async removeSingleSubject(subjectId, classId, sectionId = null) {
        try {
          await api.delete('/class-subjects', {
            params: {
              class_id: classId,
              section_id: sectionId,
              subject_ids: subjectId
            }
          });
          this.$message.success('Subject removed successfully');
          await this.fetchData();
        } catch (error) {
          console.error('Error removing subject:', error);
          this.$message.error('Failed to remove subject');
        }
      }
    }
  };
  </script>
  
  <style scoped>
  /* Background and Layout */
  .dark-bg {
    background-color: #0d1b2a;
    min-height: 100vh;
  }
  
  /* Main Card */
  .card {
    background: linear-gradient(145deg, #1b263b, #0d1b2a);
    border-radius: 20px;
  }
  
  /* Inner Cards */
  .selection-card {
    background: #1b263b;
    border: 1px solid #314e73;
  }
  
  /* Table */
  .dark-table {
    width: 100%;
    border-collapse: collapse;
    color: #dbe2ef;
  }
  
  .dark-table th,
  .dark-table td {
    padding: 12px 16px;
    border-bottom: 1px solid #314e73;
  }
  
  .dark-table th {
    background-color: #1b263b;
    font-weight: bold;
  }
  
  /* Buttons */
  .action-btn {
    border-radius: 10px;
    font-weight: 600;
  }
  
  .edit-btn {
    background-color: #1b9aaa;
    color: white;
    border-radius: 8px;
  }
  
  /* Select Dropdown */
  .custom-select >>> .el-input__inner {
    background-color: #1b263b;
    border: 1px solid #314e73;
    color: #dbe2ef;
  }
  
  .custom-select >>> .el-select-dropdown {
    background-color: #1b263b;
    color: #dbe2ef;
  }
  
  /* Tags */
  .tag {
    background-color: #0077b6;
    color: #ffffff;
  }
  .table-container {
  max-height: 500px; /* 🛡️ limit height */
  overflow-y: auto; /* 🎯 scroll vertically if content exceeds */
  overflow-x: auto; /* 🎯 scroll horizontally if needed */
  border: 1px solid #314e73;
}

/* Optional: Nice scrollbar styling */
.table-container::-webkit-scrollbar {
  width: 8px;
}
.table-container::-webkit-scrollbar-thumb {
  background: #314e73;
  border-radius: 8px;
}
.table-container::-webkit-scrollbar-track {
  background: #1b263b;
}
  </style>
  